/**
 * Wire shapes for Apple approval through Candid and the app-listing switch (GH-870).
 *
 * App Store Review Guideline 3.2.1(vi): every nonprofit listed in the app must hold Apple's
 * nonprofit approval, which for a US charity Candid decides. The platform console shows each
 * organization's status (`candidStatusWireSchema`, in v1.ts beside the admin organization row),
 * lets platform staff ask Candid again for one organization, and holds the switch that makes the
 * donor lists show only approved organizations. Everything here is platform-staff only.
 */
import { z } from "zod";
import { appListingOverrideDirectionSchema, controlChangeReasonSchema, settingLastChangeSchema, } from "./v1.js";
export const appListingCountsSchema = z.object({
    /** Approved, not archived. */
    approvedTotal: z.number().int().nonnegative(),
    /** Of those, the ones the app can list right now: "N of M approved organizations". */
    approvedListable: z.number().int().nonnegative(),
    /** Every active record the app would list with the filter on, claimed or not. */
    listableTotal: z.number().int().nonnegative(),
    /** Every active record: what the app lists with the filter off. */
    activeTotal: z.number().int().nonnegative(),
});
/** Console → Platform settings → App listing. */
export const appListingSettingSchema = z.object({
    /** Whether the donor lists carry only organizations Apple has approved. */
    requireCandidSeal: z.boolean(),
    /** Where the value comes from: a staff decision, or the deployment's environment default. */
    source: z.enum(["platform_setting", "deployment_default"]),
    /** `DIRECTORY_REQUIRE_APPLE_ELIGIBLE` as this deployment was started with it. */
    deploymentDefault: z.boolean(),
    counts: appListingCountsSchema,
    /** How long Candid's "eligible" counts, and a person's verification. */
    freshnessHours: z.number().int().positive(),
    humanVerificationDays: z.number().int().positive(),
    /** Whether this deployment can ask Candid at all (`CANDID_API_KEY` present). */
    lookupConfigured: z.boolean(),
    /** The longest a change takes to reach every donor list: server cache plus HTTP cache. */
    propagationSeconds: z.number().int().nonnegative(),
    lastChange: settingLastChangeSchema.extend({ requireCandidSeal: z.boolean() }).nullable(),
    stepUpRequired: z.boolean(),
});
export const updateAppListingSchema = z
    .object({
    requireCandidSeal: z.boolean(),
    reason: controlChangeReasonSchema,
})
    .strict();
/**
 * `POST /v1/admin/organizations/:id/candid-verify`: a platform staff member's assertion that they
 * opened `profileUrl` on Candid's own site and saw a current Seal of Transparency. Nothing here
 * asks Candid anything — it is the human-verification fallback (migration 0024), distinct from
 * the API lookup `checkCandidNow` performs.
 */
export const verifyCandidSealSchema = z
    .object({
    /** The https link to the organization's own Candid/GuideStar profile the reviewer opened. */
    profileUrl: z
        .string()
        .url()
        .regex(/^https:\/\//, "Must be a secure (https) link to the Candid profile."),
    /** Candid's seal tier, if the profile showed one (e.g. "Gold"). Free text, same as 0024. */
    sealLevel: z.string().trim().min(1).max(32).optional(),
})
    .strict();
/**
 * `POST /v1/admin/organizations/:id/listing-override`: a platform staff member's deliberate
 * override of the 3.2.1(vi) listing verdict for one organization (migration 0123, GH-892) —
 * force it listed despite failing the Candid/human check, or force it hidden despite passing.
 * This is a compliance bypass, not a lookup, so `reason` is required whenever `override` is set
 * **and** when clearing one back to `null`: the decision to stop overriding needs its own record
 * for the same accountability reasons the override itself does.
 */
export const setAppListingOverrideSchema = z
    .object({
    override: appListingOverrideDirectionSchema.nullable(),
    reason: controlChangeReasonSchema,
})
    .strict();
/** `POST /v1/jobs/refresh-candid-seals`: optional batch size. */
export const refreshCandidSealsSchema = z
    .object({
    /** How many organizations to ask about in this run; 25 per call to Candid. */
    limit: z.number().int().min(1).max(500).optional(),
})
    .strict();
export const refreshCandidSealsResponseSchema = z.object({
    /** False when nothing was asked: no key here, or another run holds the lock. */
    ran: z.boolean(),
    skipped: z.enum(["not_configured", "already_running"]).nullable(),
    asked: z.number().int().nonnegative(),
    calls: z.number().int().nonnegative(),
    eligible: z.number().int().nonnegative(),
    ineligible: z.number().int().nonnegative(),
    notFound: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    /** Set when the run stopped before its batch was done, and why. */
    stoppedEarly: z.enum(["rate_limited", "unauthorized"]).nullable(),
});
//# sourceMappingURL=candid.js.map